import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { getTokens, isExpiredToken } from '@/services/token';
import { API_V4_BASE_URL, BUILD_NUMBER } from '@/settings';

import { getRefreshToken } from './refreshToken';

const instance: AxiosInstance = axios.create();

instance.interceptors.request.use(async function (config): Promise<any> {
  const tokens = getTokens();
  let token = tokens.token;

  if (isExpiredToken() && tokens.refreshToken) {
    token = await getRefreshToken();
  }

  return {
    ...config,
    headers: {
      ...config.headers,
      Authorization: token ? `Bearer ${token}` : undefined,
      FrontVersion: BUILD_NUMBER || 'local'
    }
  };
});

instance.interceptors.response.use(
  async config => config,
  async error => {
    const { token, refreshToken } = getTokens();

    if (error.config.retries > 3) {
      return Promise.reject(error);
    }

    const isServerDown = error?.response?.status == 503;

    if (isServerDown) {
      error.config.retries = (error?.config?.retries ?? 0) + 1;

      const retryTimeout = error.config.retries * (5_000 + Math.random() * 10_000);
      await timeout(retryTimeout);
      return request(error.config);
    }

    const isPossibleRefreshToken = error?.response?.status === 401 && token && refreshToken;

    if (!isPossibleRefreshToken) {
      return Promise.reject(error);
    }

    if (isExpiredToken() && refreshToken) {
      await getRefreshToken();
    }

    if (!error.config.retry) {
      error.config.retry = true;
      return request(error.config);
    }

    return error;
  }
);

export const request = <T = any>(config: AxiosRequestConfig) => instance.request<T>(config);
export const get = <T = any>(props: AxiosRequestConfig) =>
  request<T>({
    ...props,
    method: 'get',
    url: props.url,
    baseURL: props.baseURL || API_V4_BASE_URL
  });

export const post = <T = any>(props: AxiosRequestConfig) =>
  request<T>({
    ...props,
    method: 'post',
    url: props.url,
    baseURL: props.baseURL || API_V4_BASE_URL
  });

export const put = <T = any>(props: AxiosRequestConfig) =>
  request<T>({
    ...props,
    method: 'put',
    url: props.url,
    baseURL: props.baseURL || API_V4_BASE_URL
  });

export const del = <T = any>(props: AxiosRequestConfig) =>
  request<T>({
    ...props,
    method: 'delete',
    url: props.url,
    baseURL: props.baseURL || API_V4_BASE_URL
  });

const timeout = async (timeInMilliseconds: number) => {
  return new Promise(resolve => {
    setTimeout(resolve, timeInMilliseconds);
  });
};
